import { useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Upload, X, Loader2, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* Compact inline photo cell for inventory rows. Shows a 48–64px thumbnail.
   Admin-only controls: Take photo (camera capture on mobile, file picker on
   desktop), Upload from device, Remove. Falls back to a "+" placeholder when
   no photo is set. Tap the thumbnail to view full-size in a new tab. */
export default function InventoryPhotoCell({ value, editable, onChange, token, size = 48, testId }) {
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const upload = async (blob, filename) => {
    if (!token || !blob) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, filename || 'photo.jpg');
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      const url = data?.url?.startsWith('http') ? data.url : `${BACKEND}${data.url}`;
      onChange(url);
      toast.success('Photo saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setBusy(false);
      setMenuOpen(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f, f.name);
    e.target.value = '';
  };

  const remove = () => {
    onChange('');
    setMenuOpen(false);
  };

  const dim = { width: size, height: size };

  if (!editable) {
    if (!value) {
      return (
        <div className="flex items-center justify-center rounded-md text-[#A3C4EE]" style={{ ...dim, backgroundColor: '#F0F4FA', border: '1px dashed #D9E3F1' }}>
          <ImagePlus className="w-4 h-4" />
        </div>
      );
    }
    return (
      <a href={value} target="_blank" rel="noreferrer" data-testid={testId}>
        <img src={value} alt="" style={dim} className="rounded-md object-cover" />
      </a>
    );
  }

  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        disabled={busy}
        data-testid={testId}
        title={value ? 'Edit photo' : 'Add photo'}
        className="rounded-md overflow-hidden flex items-center justify-center transition-all hover:ring-2 hover:ring-blue-400"
        style={{ ...dim, backgroundColor: value ? 'transparent' : '#F0F4FA', border: value ? '1px solid #D9E3F1' : '1px dashed #A3C4EE', color: '#003DA5' }}
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : value ? (
          <img src={value} alt="" className="w-full h-full object-cover" />
        ) : (
          <ImagePlus className="w-4 h-4" />
        )}
      </button>

      {menuOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
          <div
            className="absolute z-50 mt-1 left-0 rounded-lg overflow-hidden"
            style={{ backgroundColor: 'white', border: '1px solid #D9E3F1', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 160 }}
          >
            <MenuItem
              icon={<Camera className="w-3.5 h-3.5" />}
              label="Take photo"
              testid="inv-photo-camera"
              onClick={() => cameraRef.current?.click()}
            />
            <MenuItem
              icon={<Upload className="w-3.5 h-3.5" />}
              label="Upload from device"
              testid="inv-photo-upload"
              onClick={() => fileRef.current?.click()}
            />
            {value && (
              <MenuItem
                icon={<X className="w-3.5 h-3.5" />}
                label="Remove"
                testid="inv-photo-remove"
                onClick={remove}
                color="#DC2626"
              />
            )}
          </div>
        </>
      )}

      {/* Camera capture: capture="environment" makes mobile open the rear camera directly.
          On desktop, Chrome/Safari fall back to a regular file picker. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFile}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />
    </div>
  );
}

function MenuItem({ icon, label, onClick, testid, color = '#003DA5' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-left hover:bg-blue-50 transition-colors"
      style={{ color, fontFamily: 'Manrope' }}
    >
      {icon} {label}
    </button>
  );
}
